import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

// Theme-specific public website configurations (matching manifest-generator.js)
const themePublicWebsiteConfigs = {
  henddu: {
    title: 'Henddu Air Quality Monitoring',
    subtitle: 'Real-time Air Quality Surveillance',
    subtitleFr: "Surveillance de la Qualité de l'Air en Temps Réel",
  },
  ciapol: {
    title: 'CIAPOL - Centre Ivoirien Antipollution',
    subtitle: 'Air Quality Surveillance',
    subtitleFr: "Surveillance de la Qualité de l'Air",
  },
  atmoivoir: {
    title: 'Atmo Ivoir - Air Quality Monitoring',
    subtitle: 'Air Quality Surveillance for Ivory Coast',
    subtitleFr: "Surveillance de la Qualité de l'Air en Côte d'Ivoire",
  },
  ecoair: {
    title: 'EcoAir Quality Monitoring',
    subtitle: 'Environmental Air Quality Monitoring',
    subtitleFr: "Surveillance Environnementale de la Qualité de l'Air",
  },
  airwatch: {
    title: 'AirWatch Monitoring System',
    subtitle: 'Professional Air Quality Monitoring',
    subtitleFr: "Surveillance Professionnelle de la Qualité de l'Air",
  },
};

// Dynamic theme configuration from environment variables
function getThemeConfig(clientTheme) {
  // Get public website config for this theme
  const publicWebsiteConfig =
    themePublicWebsiteConfigs[clientTheme] || themePublicWebsiteConfigs.henddu;

  // Default configuration fallback
  const defaultConfig = {
    name: 'Henddu Air Quality Monitoring',
    shortName: 'Henddu',
    logoPath: '/images/logos/logo-henddu.png',
    faviconPath: '/logo.png',
    features: {
      advancedAnalytics: true,
      customReports: true,
      multiLanguage: true,
      correlationAnalysis: true,
      seasonalAnalysis: true,
      dataExport: true,
      realTimeAlerts: true,
    },
    colors: {
      primary: '#0D7377',
      secondary: '#40A578',
    },
    publicWebsite: publicWebsiteConfig,
  };

  // Build configuration from environment variables
  const config = {
    name: import.meta.env.VITE_APP_TITLE || defaultConfig.name,
    shortName: import.meta.env.VITE_BRAND_NAME || defaultConfig.shortName,
    logoPath: import.meta.env.VITE_CLIENT_LOGO_URL || defaultConfig.logoPath,
    faviconPath:
      import.meta.env.VITE_CLIENT_FAVICON || defaultConfig.faviconPath,
    supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
    companyUrl: import.meta.env.VITE_COMPANY_URL,
    features: {
      advancedAnalytics:
        import.meta.env.VITE_FEATURE_ADVANCED_ANALYTICS === 'true',
      customReports: import.meta.env.VITE_FEATURE_REPORTING === 'true',
      multiLanguage: import.meta.env.VITE_FEATURE_MULTI_LANGUAGE !== 'false', // Default true
      correlationAnalysis:
        import.meta.env.VITE_FEATURE_CORRELATION_ANALYSIS !== 'false', // Default true
      seasonalAnalysis:
        import.meta.env.VITE_FEATURE_SEASONAL_ANALYSIS !== 'false', // Default true
      dataExport: import.meta.env.VITE_FEATURE_DATA_EXPORT !== 'false', // Default true
      realTimeAlerts: import.meta.env.VITE_FEATURE_ALERTS === 'true',
      customBranding: import.meta.env.VITE_FEATURE_CUSTOM_BRANDING === 'true',
      whiteLabel: import.meta.env.VITE_FEATURE_WHITE_LABEL === 'true',
      ecoInsights: import.meta.env.VITE_FEATURE_ECO_INSIGHTS === 'true',
    },
    colors: {
      // These will be dynamically read from CSS variables, but keep fallbacks
      primary: defaultConfig.colors.primary,
      secondary: defaultConfig.colors.secondary,
    },
    api: {
      baseUrl: import.meta.env.VITE_API_BASE_URL,
      version: import.meta.env.VITE_API_VERSION || 'v1',
    },
    map: {
      centerLat:
        parseFloat(import.meta.env.VITE_DEFAULT_MAP_CENTER_LAT) || 5.36,
      centerLng:
        parseFloat(import.meta.env.VITE_DEFAULT_MAP_CENTER_LNG) || -4.01,
      defaultZoom: parseInt(import.meta.env.VITE_DEFAULT_MAP_ZOOM) || 7,
    },
    publicWebsite: publicWebsiteConfig,
  };

  return config;
}

export function ThemeProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [themeLoaded, setThemeLoaded] = useState(false);
  const [dynamicLogoPath, setDynamicLogoPath] = useState(null);
  const [dynamicBrandName, setDynamicBrandName] = useState(null);
  const [dynamicFavicon, setDynamicFavicon] = useState(null);

  // Get client theme from environment variable or default to 'henddu'
  const clientTheme = import.meta.env.VITE_CLIENT_THEME || 'henddu';

  // Get dynamic theme configuration
  const themeConfig = getThemeConfig(clientTheme);

  // Function to get logo path from CSS custom properties
  const getCSSLogoPath = () => {
    if (typeof window !== 'undefined') {
      const computedStyle = getComputedStyle(document.documentElement);
      const cssLogoPath = computedStyle
        .getPropertyValue('--brand-logo')
        .trim()
        .replace(/^['"]|['"]$/g, '');
      return cssLogoPath || null;
    }
    return null;
  };

  // Function to get brand properties from CSS custom properties
  const getCSSBrandProperties = () => {
    if (typeof window !== 'undefined') {
      const computedStyle = getComputedStyle(document.documentElement);
      return {
        name: computedStyle
          .getPropertyValue('--brand-name')
          .trim()
          .replace(/^['"]|['"]$/g, ''),
        shortName: computedStyle
          .getPropertyValue('--brand-short-name')
          .trim()
          .replace(/^['"]|['"]$/g, ''),
        logo: computedStyle
          .getPropertyValue('--brand-logo')
          .trim()
          .replace(/^['"]|['"]$/g, ''),
        favicon: computedStyle
          .getPropertyValue('--brand-favicon')
          .trim()
          .replace(/^['"]|['"]$/g, ''),
      };
    }
    return {};
  };

  useEffect(() => {
    // Theme CSS is already loaded by main.jsx before app renders
    // We only need to add the theme class and read CSS custom properties
    const initializeTheme = () => {
      try {
        // Add theme class to body (idempotent operation)
        document.body.className = document.body.className.replace(
          /theme-\w+/g,
          ''
        );
        document.body.classList.add(`theme-${clientTheme}`);

        // Read brand properties from CSS custom properties
        // CSS should already be loaded by main.jsx, so we can read synchronously
        const brandProps = getCSSBrandProperties();

        // Update all favicon types (DOM manipulation only - no state updates)
        if (brandProps.favicon) {
          // Update standard favicon
          let faviconLink = document.querySelector("link[rel='icon']");
          if (!faviconLink) {
            faviconLink = document.createElement('link');
            faviconLink.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(faviconLink);
          }
          faviconLink.type = 'image/svg+xml';
          faviconLink.href = brandProps.favicon;

          // Update shortcut icon
          let shortcutLink = document.querySelector(
            "link[rel='shortcut icon']"
          );
          if (!shortcutLink) {
            shortcutLink = document.createElement('link');
            shortcutLink.rel = 'shortcut icon';
            document.getElementsByTagName('head')[0].appendChild(shortcutLink);
          }
          shortcutLink.type = 'image/png';
          shortcutLink.href = brandProps.favicon;

          // Update apple-touch-icon
          let appleTouchIcon = document.querySelector(
            "link[rel='apple-touch-icon']"
          );
          if (!appleTouchIcon) {
            appleTouchIcon = document.createElement('link');
            appleTouchIcon.rel = 'apple-touch-icon';
            document
              .getElementsByTagName('head')[0]
              .appendChild(appleTouchIcon);
          }
          appleTouchIcon.href = brandProps.favicon;
        }

        // Update page title (DOM manipulation only)
        if (brandProps.name) {
          document.title = brandProps.name;

          // Update or create meta description
          let metaDescription = document.querySelector(
            "meta[name='description']"
          );
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document
              .getElementsByTagName('head')[0]
              .appendChild(metaDescription);
          }
          metaDescription.content = `${brandProps.name} - Real-time air quality monitoring and environmental data analysis platform`;
        }

        // Batch all state updates together in a single update to minimize re-renders
        // Use React's automatic batching by updating all states synchronously
        setDynamicFavicon(brandProps.favicon || null);
        setDynamicBrandName(brandProps.name || null);
        setDynamicLogoPath(brandProps.logo || null);
        setThemeLoaded(true);
        setIsLoading(false);
      } catch (error) {
        console.warn(`⚠️ Error initializing theme ${clientTheme}:`, error);
        // Fallback - mark as loaded anyway to prevent blocking
        setThemeLoaded(true);
        setIsLoading(false);
      }
    };

    // Use requestAnimationFrame to ensure CSS is fully parsed before reading properties
    // This is more reliable than setTimeout and doesn't cause delayed state updates
    requestAnimationFrame(() => {
      initializeTheme();
    });
  }, [clientTheme]);

  // Update document title when dynamic brand name changes
  useEffect(() => {
    if (dynamicBrandName) {
      document.title = dynamicBrandName;
    }
  }, [dynamicBrandName]);

  const contextValue = {
    // Theme identification
    clientTheme,
    isDefaultTheme: clientTheme === 'henddu',

    // Theme configuration (all from environment variables)
    config: themeConfig,

    // Loading states
    isLoading,
    themeLoaded,

    // Helper methods
    hasFeature: (featureName) => {
      return themeConfig.features[featureName] || false;
    },

    getThemeClass: (baseClass = '') => {
      return `${baseClass} theme-${clientTheme}`.trim();
    },

    // Brand information (CSS custom properties take precedence over environment variables)
    brandName: dynamicBrandName || themeConfig.name,
    brandShortName: themeConfig.shortName,
    logoPath: dynamicLogoPath || themeConfig.logoPath,
    faviconPath: dynamicFavicon || themeConfig.faviconPath,
    supportEmail: themeConfig.supportEmail,
    companyUrl: themeConfig.companyUrl,

    // Colors (useful for dynamic styling - will be from CSS variables)
    colors: themeConfig.colors,

    // API configuration
    api: themeConfig.api,

    // Map configuration
    map: themeConfig.map,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useClientTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useClientTheme must be used within a ClientThemeProvider');
  }
  return context;
};

// Hook for feature flags
export const useClientFeatures = () => {
  const { config } = useClientTheme();
  return config.features;
};

// Hook for checking specific features
export const useFeature = (featureName) => {
  const { hasFeature } = useClientTheme();
  return hasFeature(featureName);
};

// Export the provider with the correct name
export const ClientThemeProvider = ThemeProvider;

export default ThemeContext;
